import React from "react";
import PropTypes from "prop-types";

function AgeValidationErrorModal({
    isOpen,
    passengerType,
    passengerName,
    actualAge,
    requiredAgeRange,
    onClose,
}) {
    if (!isOpen) return null;

    const getPassengerTypeLabel = () => {
        switch (passengerType) {
            case 2:
                return "Child";
            case 3:
                return "Infant";
            default:
                return "Passenger";
        }
    };

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
                aria-hidden="true"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-8"
                role="dialog"
                aria-modal="true"
                aria-labelledby="age-error-modal-title"
            >
                <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 sm:p-8 mx-4 transform transition-all duration-300">
                    {/* Error Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 rounded-full flex items-center justify-center">
                            <svg
                                className="w-10 h-10 sm:w-12 sm:h-12 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                    </div>

                    {/* Title */}
                    <h2
                        id="age-error-modal-title"
                        className="text-xl sm:text-2xl font-semibold text-gray-900 text-center mb-3"
                    >
                        Age Validation Error
                    </h2>

                    {/* Message */}
                    <div className="text-center mb-6 space-y-2">
                        <p className="text-gray-700 text-sm sm:text-base">
                            The date of birth for{" "}
                            <span className="font-semibold text-gray-900">
                                {passengerName || `${getPassengerTypeLabel()} passenger`}
                            </span>{" "}
                            does not match the required age criteria.
                        </p>
                    </div>

                    {/* Age Details */}
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mb-6">
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-red-800 font-medium">
                                    Passenger Type:
                                </span>
                                <span className="text-sm text-red-900 font-semibold">
                                    {getPassengerTypeLabel()}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-red-800 font-medium">
                                    Current Age:
                                </span>
                                <span className="text-sm text-red-900 font-semibold">
                                    {actualAge}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-red-800 font-medium">
                                    Required Age:
                                </span>
                                <span className="text-sm text-red-900 font-semibold">
                                    {requiredAgeRange}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Info Section */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4 rounded mb-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg
                                    className="h-5 w-5 text-blue-500 mt-0.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-xs sm:text-sm text-blue-800">
                                    <span className="font-semibold">Note:</span> Please ensure the
                                    date of birth matches the passenger type. Child passengers must
                                    be between 2-18 years old, and Infant passengers must be under 2
                                    years old.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        Understood, I'll Update the DOB
                    </button>
                </div>
            </div>
        </>
    );
}

AgeValidationErrorModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    passengerType: PropTypes.number,
    passengerName: PropTypes.string,
    actualAge: PropTypes.string,
    requiredAgeRange: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

export default AgeValidationErrorModal;
